import { parseJsonObject } from "@utils/apiFailure";

export interface CategoryResponse {
    id: number;
    name: string;
    // Nullable: lower = pinned higher; null = not pinned (E4-S4).
    popularRank?: number | null;
    // Nullable: the id of the product featured on the category's browse page;
    // null = none (E4-S5, #183). Always sent; optional in the type only so the
    // category-tree fixtures and builders that predate it need not carry it.
    featuredProductId?: string | null;
    children: CategoryResponse[];
}

export interface CreateCategoryRequest {
    name: string;
    parentId?: number;
}

export interface CreateCategoryResponse {
    id: number;
}

// Mirrors the API's UpdateCategoryCommandBody (E4-S4). The categoryId is
// supplied separately as the route param.
export interface UpdateCategoryRequest {
    name: string;
    parentId?: number | null;
    popularRank?: number | null;
    // Full-replace on the server, like popularRank: an omitted field CLEARS
    // the featured product, so callers must always send the current value.
    featuredProductId?: string | null;
}

// Lightweight shape returned by GET /categories/popular (E4-S4) — only the
// fields the popular-categories carousel needs.
export interface PopularCategory {
    id: number;
    name: string;
    // The parent category's name, so a leaf named only by weight ("1g") is
    // labelled "Flower · 1g" (B-01, Sklar 2026-10-20). Null for a top-level
    // category; optional for payloads that predate the field.
    parentName?: string | null;
}

// Minimal category shape the chip components render. Both the full
// CategoryResponse tree node and the lighter PopularCategory satisfy it, so a
// single chip serves the popular row and any category tree.
export type CategoryChipData = Pick<PopularCategory, "id" | "name" | "parentName">;

// I4 (PO ruling, 2026-10-27): DELETE /categories/{id} refuses a category still
// in use with a 409 `{ error: "category_in_use", message, productCount,
// subcategoryCount }`. The counts are DIRECT: products filed under the category
// itself (inactive ones included) and its own subcategories.
const CATEGORY_IN_USE = "category_in_use";

export interface CategoryInUse {
    productCount: number;
    subcategoryCount: number;
}

const isCount = (value: unknown): value is number =>
    typeof value === "number" && Number.isInteger(value) && value >= 0;

/**
 * The delete's in-use refusal, or undefined for any other body: another code,
 * a missing or malformed count, or no use at all (the API refuses only a
 * category something uses). Keyed on the code and the counts, never the
 * sentence. Takes the raw body (ApiError.detail).
 */
export const categoryInUse = (
    body: string | undefined,
): CategoryInUse | undefined => {
    const parsed = parseJsonObject(body);

    if (parsed?.error !== CATEGORY_IN_USE) {
        return undefined;
    }

    const { productCount, subcategoryCount } = parsed;

    return isCount(productCount) &&
        isCount(subcategoryCount) &&
        productCount + subcategoryCount > 0
        ? { productCount, subcategoryCount }
        : undefined;
};
